import { createFileRoute, Link } from "@tanstack/solid-router";
import { ErrorBoundary, Show, Suspense } from "solid-js";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { ErrorCard } from "~/components/ui/ErrorCard";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useProjectQuery } from "~/lib/queries/projects";
import { useSourcesQuery } from "~/lib/queries/sources";

export const Route = createFileRoute("/_authenticated/projects/$projectId/")({
  ssr: false,
  component: ProjectOverviewPage,
});

function ProjectOverviewContent() {
  const params = Route.useParams();
  const projectQuery = useProjectQuery(() => params().projectId);
  const sourcesQuery = useSourcesQuery(
    () => params().projectId,
    () => "",
    100,
  );

  const projectName = () => projectQuery.data?.name || "Project Overview";
  const sourceCount = () => sourcesQuery.data?.sources?.length ?? 0;

  return (
    <div class="space-y-6 max-w-5xl">
      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: projectName() }]} />

      {/* Header */}
      <div>
        <h1 class="text-2xl font-bold text-foreground">
          {projectQuery.data?.name || "Loading Project..."}
        </h1>
        <Show when={projectQuery.data?.description}>
          <p class="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
            {projectQuery.data?.description}
          </p>
        </Show>
      </div>

      {/* Stats Grid */}
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Sources Stat */}
        <Link
          to="/projects/$projectId/sources"
          params={{ projectId: params().projectId }}
          class="block group rounded-xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-xs transition-all"
        >
          <div class="text-xs font-semibold uppercase tracking-wider text-muted-foreground group-hover:text-primary transition-colors">
            Sources
          </div>
          <div class="mt-2 flex items-baseline gap-2">
            <span class="text-3xl font-bold text-foreground">
              {sourcesQuery.isPending ? "..." : sourceCount()}
            </span>
            <span class="text-xs text-muted-foreground">registered</span>
          </div>
          <div class="mt-4 text-xs text-primary font-medium flex items-center gap-1">
            View all sources{" "}
            <span class="transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </div>
        </Link>

        {/* Repositories Stat (Disabled) */}
        <div class="rounded-xl border border-border bg-card/60 p-5 select-none relative opacity-70">
          <div class="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
            Repositories
          </div>
          <div class="mt-2 flex items-baseline gap-2">
            <span class="text-3xl font-bold text-muted-foreground/40">0</span>
            <span class="text-xs text-muted-foreground/40">connected</span>
          </div>
          <span class="absolute top-4 right-4 text-[10px] font-medium tracking-wider uppercase text-muted-foreground/45 bg-muted px-1.5 py-0.5 rounded border border-border/40">
            Coming Soon
          </span>
        </div>
      </div>

      {/* Quick Links / Modules section */}
      <div class="space-y-3">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Project Sections
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/projects/$projectId/sources"
            params={{ projectId: params().projectId }}
            class="flex flex-col justify-between p-5 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-xs transition-all group"
          >
            <div class="space-y-1">
              <span class="text-2xl" aria-hidden="true">
                ⎙
              </span>
              <h3 class="text-sm font-bold text-foreground group-hover:text-primary transition-colors pt-1">
                Sources
              </h3>
              <p class="text-xs text-muted-foreground leading-relaxed">
                Manage document citations, metadata, and archival contexts.
              </p>
            </div>
          </Link>

          <Tooltip>
            <TooltipTrigger class="w-full text-left focus:outline-none">
              <div class="flex flex-col justify-between p-5 rounded-xl border border-border bg-card/50 opacity-60 cursor-not-allowed select-none min-h-35">
                <div class="space-y-1">
                  <span
                    class="text-2xl text-muted-foreground/50"
                    aria-hidden="true"
                  >
                    ◫
                  </span>
                  <h3 class="text-sm font-bold text-muted-foreground/75 pt-1">
                    Repositories
                  </h3>
                  <p class="text-xs text-muted-foreground/55 leading-relaxed">
                    Connect archives, libraries, and digital databases.
                  </p>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>Coming soon</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger class="w-full text-left focus:outline-none">
              <div class="flex flex-col justify-between p-5 rounded-xl border border-border bg-card/50 opacity-60 cursor-not-allowed select-none min-h-35">
                <div class="space-y-1">
                  <span
                    class="text-2xl text-muted-foreground/50"
                    aria-hidden="true"
                  >
                    ⚙
                  </span>
                  <h3 class="text-sm font-bold text-muted-foreground/75 pt-1">
                    Settings
                  </h3>
                  <p class="text-xs text-muted-foreground/55 leading-relaxed">
                    Configure modules, team access, and export templates.
                  </p>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>Coming soon</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

function ProjectOverviewPage() {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Project dashboard error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense fallback={<div>Loading project overview...</div>}>
        <ProjectOverviewContent />
      </Suspense>
    </ErrorBoundary>
  );
}
